<template>
  <div class="account-payment-methods pt-3">
    <CAlert v-if="loadError" color="danger">{{ loadError }}</CAlert>

    <div v-if="loading" class="account-payment-methods__loading">読み込んでいます…</div>

    <template v-else-if="shown">
      <!--
        許可の入・切（2026-10-15）。**前は「利用可能機能」タブのスイッチで切り替えていた
        請求書払いの許可を、ここへ移した。**できる人・保存される値・効き方は同じ
        （back の保存の口は、中で「利用可能機能」の切り替えと同じ書き込みを通る）。

        - 押せるのは、利用可能機能を管理できる人（canEdit。back も同じ権限を要求する）。
          見るだけの担当者には［保存する］を出さず、チェックも押せない。
        - クレジットカードの行は、カード払いが使えない環境（back の cardPaymentAvailable）
          のあいだ押せず、理由を横に出す。
        - 送るのは、押した人が変えた行だけ（触っていない行まで送ると、そのあいだに別の担当者が
          変えた許可を黙って戻してしまう）。

        **運営の会社（請求の対象外）でも、この欄は出す。**前は「利用可能機能」で、その会社の
        請求書払いも入・切できた（場所が移っただけで、できる操作は減らさない）。
      -->
      <section class="payment-section payment-section--allowed">
        <h3>この会社に許可するお支払い方法</h3>
        <ul class="payment-allowed-list">
          <li
            v-for="row in allowedRows"
            :key="row.method"
            :class="['payment-allowed', 'payment-allowed--' + row.method, { 'payment-allowed--locked': !!row.lockedReason }]"
          >
            <label class="payment-allowed__choice">
              <input
                type="checkbox"
                :checked="row.checked"
                :disabled="!row.editable"
                @change="setDraft(row.method, $event)"
              >
              <span class="payment-allowed__label">{{ row.label }}</span>
            </label>
            <span v-if="row.lockedReason" class="payment-allowed__reason">{{ row.lockedReason }}</span>
          </li>
        </ul>

        <div v-if="canEdit" class="payment-section__actions">
          <CButton
            type="button"
            color="primary"
            :disabled="!canSave"
            @click="save"
          >{{ texts.save }}</CButton>
        </div>
        <p v-else class="payment-section__note">{{ texts.viewOnly }}</p>
        <!-- 保存の結果は、ボタンのすぐ下に出す（画面の上端に出すと見えない）。 -->
        <ActionResultMessage v-bind="saveResult"/>
      </section>

      <!--
        運営の会社（請求書の発行元）。請求を立てない会社なので、いまの方法とカードは
        並べない（並べると、設定が要るように読める）。
      -->
      <p v-if="shown.billingExempt" class="account-payment-methods__exempt">
        {{ texts.exempt }}
      </p>

      <section v-else class="payment-section payment-section--current">
        <h3>いまのお支払い方法</h3>
        <dl class="payment-detail-list">
          <div class="payment-current-method">
            <dt>お支払い方法</dt>
            <dd>{{ currentMethodLabel }}</dd>
          </div>
          <!-- カードが無い会社。値の欄を空にせず、「未登録」と出す。 -->
          <div v-if="!card" class="payment-card payment-card--none">
            <dt>クレジットカード</dt>
            <dd>未登録</dd>
          </div>
          <template v-else>
            <div class="payment-card payment-card--brand">
              <dt>{{ texts.cardBrandLabel }}</dt>
              <dd>{{ card.brandLabel }}</dd>
            </div>
            <div class="payment-card payment-card--last4">
              <dt>下4桁</dt>
              <dd>{{ card.last4 }}</dd>
            </div>
            <div class="payment-card payment-card--expiry">
              <dt>有効期限</dt>
              <dd>{{ card.expiryLabel }}</dd>
            </div>
            <div v-if="card.statusLabel" class="payment-card payment-card--status">
              <dt>状態</dt>
              <dd>{{ card.statusLabel }}</dd>
            </div>
          </template>
        </dl>
      </section>
    </template>
  </div>
</template>

<script>
import api from '@/services/accessManagement'
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import { safeErrorDigest, safeResponseMessage } from '@/utils/safeResponseMessage'

/**
 * 契約企業のお支払い方法（運営が見る・許可を保存する）。
 *
 * <h2>何を出すか</h2>
 *
 * <ul>
 *   <li>この会社に許可するお支払い方法 … 請求書払い・クレジットカードのチェックと［保存する］
 *       （2026-10-15。請求書払いの許可は、前は「利用可能機能」タブで入・切していた）</li>
 *   <li>いまのお支払い方法 … お客様の請求に使っている方法と、登録済みのカード
 *       （カードブランド・下4桁・有効期限・状態）。カードが無ければ「未登録」</li>
 * </ul>
 *
 * <h2>置かない物</h2>
 *
 * カードの登録・変更・削除は、運営の画面からはできない（カードを持っているのはお客様）。
 *
 * <h2>どこから見えるか</h2>
 *
 * PREIKA運営 → 契約企業一覧 → 会社の行の［管理］→「お支払い方法」タブ（「契約」の隣）。
 *
 * <h2>判定は back が返した値のまま</h2>
 *
 * 許可されているか・カードの行を押せるか・いまの方法・カードの有無は、back が1回で返す。
 * ここでは決め直さない。カード番号そのものと、決済代行の番号は、back が返さない。
 *
 * <h2>表示の世代（2026-10-15 監査 CT-1）</h2>
 *
 * 会社を選び直した・このタブを開き直したら、表示を始め直す（{@link open}）。そのたびに
 * <b>表示の世代</b>を1つ進める。保存は始めたときの世代を覚えておき、<b>世代が変わったあとに
 * 届いた保存の応答では、画面の何も変えない</b>（チェック・結果の文・処理中の状態・保存のあとの
 * 読み直し）。開き直したあとに利用者が変えたチェックを、前の保存の応答で戻さない。
 *
 * 保存のあとの読み直し（{@link readLatest} を save から呼ぶ）は世代を進めない。世代を進めるのは、
 * 利用者が始めた読み直しだけ。
 *
 * <h2>保存の結果と、そのあとの読み直しの結果は別（2026-10-15 監査 CT-2）</h2>
 *
 * <ul>
 *   <li>保存できた（back が受けた）… 送った行は、送った値をいまの値にする。続く読み直しが
 *       失敗しても、保存前の値へ戻さない（読めなかったことは上の帯で別に出す）</li>
 *   <li>断られた（back が答えた）… 保存前の値のまま。読み直せたら、その値</li>
 *   <li>応答が無い（通信が切れた。保存されたか分からない）… 読み直せたら、その値で決める
 *       （送った値が入っていれば保存できている）。読み直しも失敗したら、選んだチェックは
 *       そのまま残し、保存できた・できなかったのどちらとも言わない</li>
 * </ul>
 */

/** 支払方法の呼び名（設計の紙の行の呼び名）。 */
const METHOD_LABELS = {
  INVOICE: '請求書払い',
  CARD: 'クレジットカード'
}

/**
 * 支払方法の許可の機能コード（back の BillingService.FEATURE_* と同じ値。DB の m_feature）。
 * **「利用可能機能」タブには、この行を出さない**（入・切はこのタブ。2026-10-15）。
 * 口座振替は、back が「利用可能機能」の一覧からも外している（引き落とす仕組みが無い）。
 */
export const PAYMENT_METHOD_FEATURE_CODES = [
  'BILLING_PAY_BANK_TRANSFER',
  'BILLING_PAY_INVOICE',
  'BILLING_PAY_CARD'
]

/** カード会社の名前。カード会社の表記のまま出す（文ではない）。 */
const BRAND_LABELS = {
  visa: 'VISA',
  mastercard: 'Mastercard',
  jcb: 'JCB',
  amex: 'American Express',
  diners: 'Diners Club',
  discover: 'Discover',
  unionpay: 'UnionPay'
}

/**
 * 画面の文。**Gemini と決めた文のまま（2026-10-11 の P4〜P10、2026-10-15 の Q1〜Q7）。言い換えない。**
 * 有効期限は「2030年12月」の形（P11。「12/30」は12月30日と読み違えるので使わない）。
 *
 * P1（許可）・P2（未許可）・P3（このタブは表示専用の案内）は、このタブで入・切できるように
 * なったので使わない（2026-10-15）。P9 は Q7 に決め直した（運営の会社でも上の欄で入・切できるので、
 * 「設定はありません」と並ぶと食い違う）。
 */
export const PAYMENT_METHOD_TEXTS = {
  /* 24：カードの種類の見出し。 */
  cardBrandLabel: 'カードブランド',
  /* 24：カード会社が分からないとき。 */
  cardBrandUnknown: '不明',
  /* 24：カードの状態の呼び名（使える）。 */
  cardActive: '有効',
  /* 24：カードの状態の呼び名（お客様の登録し直しが要る）。 */
  cardUpdateRequired: '要再登録',
  /* 24：いまの方法が、画面の知らない方法だったとき。 */
  methodUnknown: '表示できません（開発へ連絡）',
  /* 26：運営の会社（請求書の発行元）を開いたとき。下の欄の代わりに出す（Q7）。 */
  exempt: '自社アカウントのため、「いまのお支払い方法」の表示はありません。',
  /* 読み込めなかったとき。 */
  loadFailed: 'お支払い方法の情報を読み込めませんでした。時間をおいて再度お試しください。',
  /* 22：保存のボタン（Q1）。 */
  save: 'お支払い方法を保存',
  /* 22：保存できたとき（Q2）。 */
  saved: 'お支払い方法の変更を保存しました。',
  /* 22：保存できなかったとき。back が理由を返したときは、その文を出す（Q3）。 */
  saveFailed: '保存に失敗しました。時間を置いて再度お試しください。',
  /* 保存の応答が無く、続く読み直しもできず、保存できたか分からないとき（Gemini 2026-10-15 Q8）。 */
  saveUnconfirmed: '変更が適用されたか確認できませんでした。時間をおいてタブを開き直し、設定を確認してください。',
  /* 21：カード払いの設定がそろっていないあいだ、カードの行の横に出す理由（Q4）。 */
  cardLocked: 'クレジットカード決済の初期設定が完了していないため選択できません。',
  /* 23：見るだけの人。ボタンの代わりに出す（Q5）。 */
  viewOnly: '変更権限がないため、お支払い方法の設定を変更できません。',
  /* 25：「利用可能機能」タブの、前に請求書払いのスイッチがあった所（Q6）。 */
  featuresMoved: '※お支払い方法（請求書払い・クレジットカード）の設定は［お支払い方法］タブへ移動しました。'
}

const CARD_STATUS_LABELS = {
  ACTIVE: PAYMENT_METHOD_TEXTS.cardActive,
  UPDATE_REQUIRED: PAYMENT_METHOD_TEXTS.cardUpdateRequired
}

/** 結果の欄を空にする値（部品は何も出さない）。 */
const NO_RESULT = { type: 'success', text: '' }

/** 送る鍵の名前（back の PaymentMethodsForm と同じ）。 */
const PAYLOAD_KEYS = { INVOICE: 'invoiceAllowed', CARD: 'cardAllowed' }

/** back の答えから、方法ごとの「いま許可されているか」を作る。返らなかった方法は許可なし。 */
function allowedOf (read) {
  const rows = read && Array.isArray(read.allowedMethods) ? read.allowedMethods : []
  const allowed = {}
  for (const method of Object.keys(METHOD_LABELS)) {
    const row = rows.find(one => one && one.method === method)
    allowed[method] = !!(row && row.allowed === true)
  }
  return allowed
}

/** 送った中身に入っている方法（入れなかった方法は「変えない」）。 */
function sentMethods (payload) {
  return Object.keys(PAYLOAD_KEYS).filter(method => typeof payload[PAYLOAD_KEYS[method]] === 'boolean')
}

/**
 * back が保存を受けたあとの値。**送った行だけ、送った値に置きかえる**（ほかの行・いまの方法・
 * カードは、前に読めた値のまま）。保存の口は中身を返さないので、送った値で反映する。
 */
function withSaved (read, payload) {
  const methods = sentMethods(payload)
  const rows = read && Array.isArray(read.allowedMethods) ? read.allowedMethods : []
  return Object.assign({}, read, {
    allowedMethods: rows
      .filter(row => !(row && methods.includes(row.method)))
      .concat(methods.map(method => ({ method, allowed: payload[PAYLOAD_KEYS[method]] })))
  })
}

/** 読めた値に、送った行が全部、送った値で入っているか。 */
function reflectsSaved (read, payload) {
  const allowed = allowedOf(read)
  return sentMethods(payload).every(method => allowed[method] === payload[PAYLOAD_KEYS[method]])
}

/**
 * 保存の要求が失敗したときの分け方（監査 CT-3）。**保存しなかったと確定できる答えだけを「断られた」にする。**
 * <ul>
 *   <li>400番台 … 入力の不備・権限が無い・競合・ログイン切れなどで、受け付けずに返した → 'refused'</li>
 *   <li>それ以外 … 応答が無い、500番台（中継が上流から答えをもらえなかった 502・時間内に来なかった 504 を
 *       含む）。保存の途中で切れたのか、保存したあとで切れたのか分からない → 'unknown'（読み直しで決める）</li>
 * </ul>
 * ログインなどが使う共通の判定（passkey.js の isNetworkFailure）は変えない。
 */
function saveOutcomeOf (error) {
  const status = error && error.response ? Number(error.response.status) : NaN
  return status >= 400 && status < 500 ? 'refused' : 'unknown'
}

export default {
  name: 'AccountPaymentMethods',

  components: { ActionResultMessage },

  props: {
    accountId: { type: Number, required: true },
    /**
     * 読み直しの番号。親は「会社を選び直した回」と「このタブを開いた回」を組にして渡す。
     * **同じ会社でも、変われば読み直す**（お客様がカードを登録したあとに開いたとき、
     * 前の値を見せない）。
     */
    reloadKey: { type: [Number, String], default: 0 },
    /**
     * 許可を変えられるか（利用可能機能を管理できる人＝FEATURE_MANAGE）。親が
     * 「利用可能機能」のスイッチと同じ判定（canUpdateProviderFeatures）で渡す。
     * back も同じ権限を要求する。
     */
    canEdit: { type: Boolean, default: false }
  },

  data () {
    return {
      texts: PAYMENT_METHOD_TEXTS,
      /* back が返した値。読めていないあいだは null（前の会社の値を残さない）。 */
      shown: null,
      loading: true,
      loadError: '',
      /* 押した人が選んでいる値（方法 → 許可するか）。保存するまで back の値とは別に持つ。 */
      draft: {},
      saving: false,
      saveResult: NO_RESULT
    }
  },

  computed: {
    /** 読み直しのきっかけ。**会社の番号と、読み直しの番号の組。** */
    loadKey () {
      return this.accountId + ':' + this.reloadKey
    },

    /** back が返した、いま許可されている値。 */
    savedAllowed () {
      return allowedOf(this.shown)
    },

    /** カードの行を押せるか（カード払いが使える環境か）。back の答えのまま。 */
    cardSelectable () {
      return !!(this.shown && this.shown.cardPaymentAvailable === true)
    },

    /**
     * 許可の行。**請求書払い・クレジットカードの2行を、いつも出す。**
     * back が行を返さなかった方法は「許可していない」（許可が1つも無い会社でも2行出る）。
     */
    allowedRows () {
      return Object.keys(METHOD_LABELS).map(method => {
        const locked = method === 'CARD' && !this.cardSelectable
        return {
          method,
          label: METHOD_LABELS[method],
          checked: this.draft[method] === true,
          editable: this.canEdit && !this.saving && !locked,
          lockedReason: locked ? PAYMENT_METHOD_TEXTS.cardLocked : ''
        }
      })
    },

    /**
     * 送る中身。**変えた行だけ**（null・入れない＝変えない）。押せない行は変わらない。
     * 鍵の名前は back の PaymentMethodsForm と同じ。
     */
    changes () {
      const changes = {}
      for (const row of this.allowedRows) {
        if (row.method === 'CARD' && !this.cardSelectable) continue
        if (row.checked !== this.savedAllowed[row.method]) {
          changes[PAYLOAD_KEYS[row.method]] = row.checked
        }
      }
      return changes
    },

    canSave () {
      return this.canEdit && !this.saving && Object.keys(this.changes).length > 0
    },

    currentMethodLabel () {
      const method = this.shown ? this.shown.currentMethod : null
      return METHOD_LABELS[method] || PAYMENT_METHOD_TEXTS.methodUnknown
    },

    /** 登録済みのカード。無ければ null（画面は「未登録」を出す）。 */
    card () {
      const card = this.shown ? this.shown.card : null
      if (!card || !card.last4) return null
      const brand = String(card.brand || '').toLowerCase()
      return {
        brandLabel: BRAND_LABELS[brand] || PAYMENT_METHOD_TEXTS.cardBrandUnknown,
        last4: String(card.last4),
        expiryLabel: this.expiryLabel(card.expYear, card.expMonth),
        statusLabel: CARD_STATUS_LABELS[card.status] || ''
      }
    }
  },

  watch: {
    loadKey: {
      immediate: true,
      handler () {
        this.open()
      }
    }
  },

  methods: {
    /** 有効期限。「2030年12月」の形（年と月だけ。日は無い）。 */
    expiryLabel (year, month) {
      const y = Number(year)
      const m = Number(month)
      if (!Number.isInteger(y) || !Number.isInteger(m) || m < 1 || m > 12) return ''
      return `${y}年${m}月`
    },

    /** チェックを入れた・外した。前の保存の結果は消す（いま押した結果に見えるので）。 */
    setDraft (method, event) {
      const checked = !!(event && event.target && event.target.checked)
      this.draft = Object.assign({}, this.draft, { [method]: checked })
      this.saveResult = NO_RESULT
    },

    /**
     * 表示を始め直す（会社を選び直した・このタブを開き直した）。**表示の世代を1つ進める。**
     *
     * 前の世代の保存がまだ返っていなくても、処理中を解いて、いま読んだ値を出す（タブの移動は
     * 止めない）。前の世代の保存の応答は、あとで届いても画面を変えない（save の replaced）。
     */
    async open () {
      // 画面に出さない数なので data に置かない（描き直しを起こさない）。
      this.$_viewGeneration = (this.$_viewGeneration || 0) + 1
      this.loading = true
      this.loadError = ''
      this.shown = null
      this.draft = {}
      this.saving = false
      this.saveResult = NO_RESULT
      const reread = await this.readLatest()
      if (!reread) return
      if (reread.read) {
        this.shown = reread.read
        this.draft = allowedOf(reread.read)
      } else {
        this.loadError = PAYMENT_METHOD_TEXTS.loadFailed
      }
      this.loading = false
    },

    /**
     * 1回読む。**使ってよいのは、いま開いている会社の、いちばん新しく始めた読み込みだけ。**
     * 待つあいだに別の会社の［管理］を押した・開き直した（ほかの読み込みが始まった）ら null
     * （返ってきた値も失敗も捨てる。前の会社のお支払い方法を、いまの会社の欄に出さない）。
     *
     * 読めたら { read }、読めなかったら { read: null }。**画面は変えない**（読めた・読めなかった
     * ときに何を出すかは、呼んだ側が決める。開いたときと、保存のあとでは違う）。
     */
    async readLatest () {
      // 画面に出さない数なので data に置かない（描き直しを起こさない）。
      const seq = this.$_paymentSeq = (this.$_paymentSeq || 0) + 1
      const target = this.accountId
      const stale = () => seq !== this.$_paymentSeq || target !== this.accountId
      try {
        const read = await api.getPaymentMethods(target)
        if (stale()) return null
        if (!read || typeof read !== 'object') throw new Error('empty')
        return { read }
      } catch (error) {
        if (stale()) return null
        this.$emit('load-error', safeErrorDigest(error))
        return { read: null }
      }
    },

    /**
     * 許可を保存する。**保存を始めたときの会社と表示の世代を覚えておく。**終わったときに
     * 会社を選び直していた・このタブを開き直していたら、いまの画面には何もしない
     * （成功でも失敗でも。チェック・結果の文・処理中の状態・保存のあとの読み直しのどれも）。
     *
     * 保存の要求の結果（1.）と、そのあとの読み直しの結果（2.）は分けて扱う。
     * <ul>
     *   <li>保存できた … 送った行は送った値にする。読み直せたら、その値。読めなくても保存前へ戻さない</li>
     *   <li>断られた（400番台。保存しなかったと確定できる答え）… 読み直せたら、その値。読めなければ保存前の値へ戻す</li>
     *   <li>保存されたか分からない（応答が無い・500番台。502・504 を含む）… 読み直せたら、その値で決める。
     *       読めなければ、選んだチェックのまま残し、保存できた・できなかったのどちらとも言わない</li>
     * </ul>
     */
    async save () {
      if (!this.canSave) return
      const target = this.accountId
      const generation = this.$_viewGeneration
      const replaced = () => generation !== this.$_viewGeneration || target !== this.accountId
      const payload = Object.assign({}, this.changes)
      this.saving = true
      this.saveResult = NO_RESULT
      try {
        // 1. 保存の要求。
        let outcome = 'saved'
        let refusal = null
        try {
          await api.savePaymentMethods(target, payload)
        } catch (error) {
          outcome = saveOutcomeOf(error)
          refusal = error
        }
        if (replaced()) return
        if (outcome === 'saved') {
          // 送った行は、送った値がいまの値（このあと読めなくても、保存前の値へ戻さない）。
          this.shown = withSaved(this.shown, payload)
          this.draft = allowedOf(this.shown)
        }

        // 2. そのあとの読み直し。開き直したあとなら、読めた値も失敗も使わない。
        const reread = await this.readLatest()
        if (!reread || replaced()) return
        if (reread.read) {
          this.loadError = ''
          this.shown = reread.read
          this.draft = allowedOf(reread.read)
        } else {
          // ほかの行・いまの方法・カードの最新が取れていないことは、上の帯で出す。
          this.loadError = PAYMENT_METHOD_TEXTS.loadFailed
          // 断られた：保存前の値へ戻す（選んだチェックを、保存されたように見せない）。
          // 保存できた：送った値のまま（1. で反映ずみ）。保存されたか分からない：選んだチェックのまま。
          if (outcome === 'refused') this.draft = allowedOf(this.shown)
        }
        this.saveResult = this.saveResultOf(outcome, refusal,
          reread.read ? reflectsSaved(reread.read, payload) : null)
      } finally {
        if (!replaced()) this.saving = false
      }
    },

    /**
     * 保存の結果の文。applied … 読み直せたとき、送った行が送った値で入っていたか
     * （読み直せなかったら null）。
     */
    saveResultOf (outcome, refusal, applied) {
      if (outcome === 'saved' || (outcome === 'unknown' && applied === true)) {
        return { type: 'success', text: PAYMENT_METHOD_TEXTS.saved }
      }
      if (outcome === 'refused' || applied === false) {
        return { type: 'danger', text: safeResponseMessage(refusal, PAYMENT_METHOD_TEXTS.saveFailed) }
      }
      // 保存されたか分からず（応答が無い・500番台）、読み直しもできなかった。できた・できなかったのどちらとも
      // 言わず、確かめられなかったことと次にすることだけを出す（読めなかったことは上の帯にも出ている）。
      return { type: 'info', text: PAYMENT_METHOD_TEXTS.saveUnconfirmed }
    }
  }
}
</script>

<style scoped>
.account-payment-methods { min-width: 0; }
.account-payment-methods__loading { color: #5c6873; }
.account-payment-methods__exempt { margin: 0; color: #17384d; }

.payment-section { margin-bottom: 24px; }
.payment-section h3 { margin: 0 0 10px; color: #17384d; font-size: 16px; font-weight: 700; }
.payment-section__note { margin: 10px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }
.payment-section__actions { margin-top: 12px; }

.payment-allowed-list { display: grid; gap: 4px; margin: 0; padding: 0; max-width: 520px; list-style: none; }
.payment-allowed {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 16px;
  padding: 8px 0;
  border-bottom: 1px solid #ebedef;
}
.payment-allowed__choice {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: #17384d;
  font-size: 14px;
  font-weight: 600;
}
.payment-allowed__choice input { width: 16px; height: 16px; margin: 0; }
.payment-allowed--locked .payment-allowed__label { color: #8a93a2; }
.payment-allowed__reason { color: #6a7f8c; font-size: 12px; line-height: 1.6; overflow-wrap: anywhere; }

.payment-detail-list { display: grid; gap: 8px; margin: 0; max-width: 520px; }
.payment-detail-list > div {
  display: grid;
  grid-template-columns: minmax(8rem, 12rem) minmax(0, 1fr);
  gap: 12px;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px solid #ebedef;
}
.payment-detail-list dt { margin: 0; color: #5c6873; font-size: 13px; font-weight: 600; }
.payment-detail-list dd { margin: 0; color: #17384d; font-size: 14px; overflow-wrap: anywhere; }

@media (max-width: 575.98px) {
  .payment-detail-list > div { grid-template-columns: minmax(0, 1fr); gap: 2px; }
}
</style>
